/**
 * Leave Privacy Helper
 *
 * Hides specific leave type details (paid vs unpaid, specific sick leave types)
 * from regular employees when viewing other people's leave.
 *
 * Privacy rules:
 * - Owners, admins, and approvers can see full leave type details
 * - Employees can see full details of their OWN leave
 * - Employees see simplified leave types for others (e.g., "Sick Leave" instead of "Sick Leave (Full Pay)")
 *
 * This protects employee privacy - whether someone took paid or unpaid vacation,
 * or the specific type of sick leave, should only be visible to HR/admins/approvers.
 */

// Roles that can see all leave details
const PRIVILEGED_ROLES = ['owner', 'admin', 'approver'];

// Generic category colors for non-privileged viewers
const PRIVACY_COLORS = {
  vacation: '#3B82F6',  // Blue for all vacation types
  sick: '#EF4444',      // Red for all sick types
  other: '#6B7280',     // Gray for other types
};

// Keywords to categorize leave types
const VACATION_KEYWORDS = ['vacation', 'annual', 'holiday', 'pto', 'time off', 'time-off', 'unpaid vacation'];
const SICK_KEYWORDS = ['sick', 'medical', 'illness', 'health'];

// Patterns to remove from leave type names for privacy (e.g., "(Full Pay)", "(Half Pay)")
const PRIVACY_PATTERNS = [
  /\s*\(full\s*pay\)/i,
  /\s*\(half\s*pay\)/i,
  /\s*\(unpaid\)/i,
  /\s*\(paid\)/i,
];

/**
 * Check if a role is privileged (can see all leave details)
 */
function isPrivilegedRole(role) {
  if (!role) return false;
  return PRIVILEGED_ROLES.includes(role.toLowerCase());
}

/**
 * Determine the privacy category for a leave type
 */
function getLeaveCategory(leaveTypeName) {
  if (!leaveTypeName) return 'other';
  const lower = leaveTypeName.toLowerCase();

  if (VACATION_KEYWORDS.some(kw => lower.includes(kw))) {
    return 'vacation';
  }
  if (SICK_KEYWORDS.some(kw => lower.includes(kw))) {
    return 'sick';
  }
  return 'other';
}

/**
 * Strip sensitive suffixes from leave type name (e.g., "(Full Pay)")
 */
function stripSensitiveSuffix(leaveTypeName) {
  if (!leaveTypeName) return leaveTypeName;
  let result = leaveTypeName;
  for (const pattern of PRIVACY_PATTERNS) {
    result = result.replace(pattern, '');
  }
  return result.trim();
}

/**
 * Get the display name for a leave type, respecting privacy settings
 *
 * @param {string} leaveTypeName - The actual leave type name
 * @param {string} viewerRole - The role of the user viewing the leave (owner, admin, approver, employee)
 * @param {boolean} isOwnLeave - Whether this is the viewer's own leave
 * @returns {string} - The name to display
 */
export function getPrivacyAwareLeaveTypeName(leaveTypeName, viewerRole, isOwnLeave = false) {
  // Privileged roles (owner, admin, approver) can see everything
  if (isPrivilegedRole(viewerRole)) {
    return leaveTypeName;
  }

  // Users can always see details of their own leave
  if (isOwnLeave) {
    return leaveTypeName;
  }

  // For regular employees viewing others' leave, show generic category names
  const category = getLeaveCategory(leaveTypeName);
  switch (category) {
    case 'vacation':
      // Show "Vacation" for all vacation-related types (paid, unpaid, etc.)
      return 'Vacation';
    case 'sick':
      // Show "Sick Leave" for all sick-related types (full pay, half pay, etc.)
      return 'Sick Leave';
    default:
      // For other leave types, strip sensitive suffixes but keep the base name
      return stripSensitiveSuffix(leaveTypeName);
  }
}

/**
 * Get the display color for a leave type, respecting privacy settings
 *
 * @param {string} leaveTypeName - The leave type name
 * @param {string} actualColor - The actual color assigned to this leave type
 * @param {string} viewerRole - The role of the user viewing the leave
 * @param {boolean} isOwnLeave - Whether this is the viewer's own leave
 * @returns {string} - The color to display
 */
export function getPrivacyAwareLeaveColor(leaveTypeName, actualColor, viewerRole, isOwnLeave = false) {
  // Always use the actual color from the database
  // Color doesn't reveal sensitive info (paid/unpaid) - that's handled by name privacy
  // This allows admins to set custom colors for leave types that all users can see
  return actualColor || PRIVACY_COLORS.other;
}

/**
 * Check if the viewer should see detailed leave type info
 */
export function canSeeLeaveDetails(viewerRole, isOwnLeave = false) {
  return isPrivilegedRole(viewerRole) || isOwnLeave;
}
